/**
 * The resolved-outcome view of the member resignation step (056). Copy is
 * chosen by copy SECTION, not by `status` alone (member-surface §3 row 7), and
 * each section carries its own role (member-surface §4):
 *
 *   §6  ok                                    → status, success box
 *   §7  refused / already-pending             → status, informative box (NOT an error)
 *   §8b everything else, incl. `unconfirmed`  → status, neutral box
 *
 * Presentation only: the step owns submission state and the focus move.
 */

import type { JSX } from 'react';

import { fill } from '../../../i18n/fill.js';
import { useLanguage } from '../../../i18n/useLanguage.js';
import { useTranslate } from '../../../i18n/useTranslate.js';
import { formatMessageDateTime } from '../../../member-cases/formatMessageDateTime.js';
import type { SubmitResignationOutcome } from '../../../membership-resignation/MembershipResignationPort.js';

const boxBaseClasses =
  'flex flex-col gap-2 rounded-md border p-4 text-left text-sm text-foreground';
const successBoxClasses = `${boxBaseClasses} border-primary bg-primary/10`;
const pendingBoxClasses = `${boxBaseClasses} border-primary/50 bg-card`;
const unresolvedBoxClasses = `${boxBaseClasses} border-border bg-muted`;
const paragraphClasses = 'm-0';

export function ResignationOutcome({
  outcome,
}: {
  readonly outcome: SubmitResignationOutcome;
}): JSX.Element {
  const t = useTranslate();
  const { active } = useLanguage();

  if (outcome.status === 'ok') {
    // The register's own time, in the device zone (D-232(e)). No end date:
    // the register sets it by rules Oma+ does not hold (FR-016).
    return (
      <div role="status" className={successBoxClasses}>
        <p className={paragraphClasses}>
          {fill(t('compose.wizard.resignation.success.submitted'), {
            date: formatMessageDateTime(outcome.submittedAt, active),
          })}
        </p>
        <p className={paragraphClasses}>{t('compose.wizard.resignation.success.continues')}</p>
      </div>
    );
  }

  if (outcome.status === 'refused' && outcome.reason === 'already-pending') {
    // Reassurance, not a failure (FR-009): the register already holds one.
    return (
      <div role="status" className={pendingBoxClasses}>
        <p className={paragraphClasses}>{t('compose.wizard.resignation.alreadyPending')}</p>
      </div>
    );
  }

  // Unresolved: neither success nor failure (FR-012).
  return (
    <div role="status" className={unresolvedBoxClasses}>
      <p className={paragraphClasses}>{t('compose.wizard.resignation.unresolved')}</p>
    </div>
  );
}
